import { computed } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { makeObservableRowBase, RowBase } from 'shared/ui/TableNew/model/RowBase'
import { AnalyticContact } from 'entities/Chatbot/model/AnalyticContact'
import {
  ChatbotConversationStatus,
  type IChatbotAnalyticBase,
  type IChatbotAnalyticQuietHoursItem,
} from './type'
import type { IChatbotAnalyticListItemApi } from '../api/chatbotTypes'

const TIME_FORMAT = 'MMM D, YYYY - h:mm A'

function isQuietHoursItem(
  item: IChatbotAnalyticListItemApi
): item is IChatbotAnalyticQuietHoursItem {
  return 'core_conversation_id' in item && !('status' in item)
}

type IRowOrigin = (IChatbotAnalyticQuietHoursItem & { id: number }) | IChatbotAnalyticBase

function toRowOrigin(item: IChatbotAnalyticListItemApi) {
  if (isQuietHoursItem(item)) {
    return { ...item, id: item.core_conversation_id }
  }
  return item
}

export class ChatbotAnalyticListItem extends RowBase<IRowOrigin> {
  constructor(item: IChatbotAnalyticListItemApi) {
    super(toRowOrigin(item))

    makeObservableRowBase(this, {
      targetTime: computed,
      createdTime: computed,
      contact: computed,
      status: computed,
      sendFrom: computed,
    })
  }

  get targetTime() {
    if (isQuietHoursItem(this.origin)) {
      return this.timeFormat(this.origin.message_send_at)
    }
    return this.timeFormat(this._getTargetTime(this.origin))
  }

  get createdTime() {
    if (isQuietHoursItem(this.origin)) {
      return this.timeFormat(this.origin.message_created_at)
    }
    return this.timeFormat(this.origin.created_at)
  }

  get contact() {
    return this.origin.contact ? new AnalyticContact(this.origin.contact) : null
  }

  get status() {
    return isQuietHoursItem(this.origin) ? ChatbotConversationStatus.QuietHours : this.origin.status
  }

  get sendFrom() {
    return isQuietHoursItem(this.origin) ? undefined : this.origin.send_from ?? null
  }

  timeFormat = (time: string) => {
    return uiStore.dayjs(time).format(TIME_FORMAT)
  }

  private _getTargetTime(item: IChatbotAnalyticBase) {
    switch (item.status) {
      case ChatbotConversationStatus.Terminated:
      case ChatbotConversationStatus.InProgress:
        return item.created_at
      case ChatbotConversationStatus.Abandoned:
        return item.fallback_at
      case ChatbotConversationStatus.Completed:
        return item.success_at
    }
  }
}
